import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import { FileText, Upload, Eye, Trash2, Download, CheckCircle2, Circle, Loader2, Plane, CalendarClock, AlertTriangle } from "lucide-react";
import DocumentPreviewModal from "@/components/DocumentPreviewModal";
import DateInput from "@/components/DateInput";

const WORKER_FIELDS = ["rut", "first_name", "last_name", "email", "phone", "whatsapp", "city",
  "position", "company", "project", "assigned_user", "expected_hire_date", "due_date", "whatsapp_opt_in", "visa_temporaria"];

export default function WorkerDocuments({ worker, canEdit, onChanged, onSummary, hideFolderCard }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [visa, setVisa] = useState(!!worker.visa_temporaria);
  const [preview, setPreview] = useState(null);
  const [expiryPrompt, setExpiryPrompt] = useState(null); // { doc, file, date }
  const fileRefs = useRef({});

  const load = () => api.get(`/workers/${worker.id}/documents`).then((r) => { setSummary(r.data); onSummary && onSummary(r.data); }).catch(() => {});
  useEffect(() => { setVisa(!!worker.visa_temporaria); load(); }, [worker.id]);

  const toggleVisa = async (val) => {
    setVisa(val);
    const payload = {}; WORKER_FIELDS.forEach((k) => (payload[k] = worker[k] ?? ""));
    payload.visa_temporaria = val;
    payload.whatsapp_opt_in = worker.whatsapp_opt_in ?? true;
    try { await api.put(`/workers/${worker.id}`, payload); toast.success(val ? "Visa temporaria activada — Pasaporte requerido" : "Visa temporaria desactivada"); load(); onChanged && onChanged(); }
    catch (e) { toast.error("No se pudo actualizar"); setVisa(!val); }
  };

  const doUpload = async (code, file, expiration) => {
    setBusy(code);
    const fd = new FormData();
    fd.append("file", file);
    if (expiration) fd.append("expiration_date", expiration);
    try { const { data } = await api.post(`/workers/${worker.id}/documents/${code}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setSummary(data); toast.success("Documento cargado"); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo cargar"); }
    finally { setBusy(null); }
  };

  const upload = async (doc, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Solo se permiten archivos PDF"); return; }
    if (doc.has_expiration) {
      setExpiryPrompt({ doc, file, date: "" });
      return;
    }
    await doUpload(doc.code, file, null);
  };

  const confirmExpiry = async () => {
    if (!expiryPrompt?.date) { toast.error("Debes indicar la fecha de vencimiento"); return; }
    const { doc, file, date } = expiryPrompt;
    setExpiryPrompt(null);
    await doUpload(doc.code, file, date);
  };

  const remove = async (code) => {
    setBusy(code);
    try { const { data } = await api.delete(`/workers/${worker.id}/documents/${code}`); setSummary(data); toast.success("Documento eliminado"); onChanged && onChanged(); }
    catch (e) { toast.error("No se pudo eliminar"); }
    finally { setBusy(null); }
  };

  const view = (code) => {
    const uploaded = (summary?.items || []).filter((d) => d.uploaded);
    const items = uploaded.map((d) => ({
      url: `/workers/${worker.id}/documents/${d.code}/file`,
      title: d.name,
      filename: d.original_filename || `${d.code}.pdf`,
    }));
    const idx = uploaded.findIndex((d) => d.code === code);
    setPreview({ items, index: idx < 0 ? 0 : idx });
  };

  const downloadConsolidated = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(`/workers/${worker.id}/documents/consolidated/pdf`, { responseType: "blob" });
      const exceeds = resp.headers["x-pdf-exceeds-limit"] === "true";
      const size = parseInt(resp.headers["x-pdf-size"] || "0", 10);
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `carpeta_${worker.rut || worker.id}.pdf`; a.click();
      URL.revokeObjectURL(url);
      if (exceeds) toast.warning(`PDF generado (${(size / 1048576).toFixed(2)} MB) — excede el máximo de 2 MB pese a la compresión.`);
      else toast.success(`Carpeta descargada (${(size / 1024).toFixed(0)} KB)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "La carpeta no está completa");
    } finally { setDownloading(false); }
  };

  if (!summary) return <div className="flex justify-center py-10 text-slate-400"><Loader2 className="animate-spin" /></div>;

  const color = summary.progress >= 100 ? "bg-emerald-500" : summary.progress >= 50 ? "bg-blue-500" : "bg-amber-500";

  return (
    <div className="space-y-5">
      {/* Visa toggle */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
        <div className="flex items-center gap-2"><Plane size={16} className="text-blue-600" /><div><p className="text-sm font-medium text-slate-700">Visa temporaria</p><p className="text-xs text-slate-400">Si está activa, el Pasaporte es obligatorio.</p></div></div>
        <Switch data-testid="visa-toggle" checked={visa} disabled={!canEdit} onCheckedChange={toggleVisa} />
      </div>

      {/* Folder progress */}
      {!hideFolderCard && (
      <div className="p-4 rounded-xl border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Carpeta de documentos</span>
          <span className="text-sm font-display font-bold text-slate-800">{summary.uploaded}/{summary.total} · {summary.progress}%</span>
        </div>
        <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${summary.progress}%` }} /></div>
        <button
          data-testid="download-consolidated-pdf"
          disabled={!summary.complete || downloading}
          onClick={downloadConsolidated}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
        >
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Descargar carpeta en 1 PDF (máx. 2 MB)
        </button>
        {!summary.complete && <p className="text-xs text-slate-400 text-center mt-2">Disponible cuando la carpeta esté al 100%.</p>}
      </div>
      )}
      {hideFolderCard && summary.complete && (
        <button
          data-testid="download-consolidated-pdf"
          disabled={downloading}
          onClick={downloadConsolidated}
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
        >
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Descargar carpeta en 1 PDF (máx. 2 MB)
        </button>
      )}

      {/* Document list */}
      <div className="space-y-2">
        {summary.items.map((d) => {
          const expDate = d.expiration_date ? new Date(d.expiration_date + "T00:00:00") : null;
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const daysLeft = expDate ? Math.ceil((expDate - today) / (1000 * 60 * 60 * 24)) : null;
          const expired = daysLeft !== null && daysLeft < 0;
          const soon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
          return (
          <div key={d.code} data-testid={`document-row-${d.code}`} className="flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-100">
            <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${d.uploaded ? "bg-emerald-50" : "bg-slate-50"}`}>
              {d.uploaded ? <CheckCircle2 size={17} className="text-emerald-600" /> : <Circle size={17} className="text-slate-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5 flex-wrap">
                {d.name}
                {d.required === false && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 rounded">opcional</span>}
                {d.condition === "visa_temporaria" && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 rounded">visa</span>}
                {d.has_expiration && <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 rounded inline-flex items-center gap-0.5"><CalendarClock size={10} /> vence</span>}
              </p>
              {d.uploaded ? (
                <p className="text-xs text-slate-400 truncate">
                  {d.original_filename} · {(d.size / 1024).toFixed(0)} KB · {formatDate(d.uploaded_at)}
                  {expDate && (
                    <span className={`ml-2 inline-flex items-center gap-1 ${expired ? "text-red-600 font-semibold" : soon ? "text-amber-600 font-semibold" : "text-slate-500"}`}>
                      <AlertTriangle size={11} /> Vence {formatDate(d.expiration_date)}{expired ? " (vencido)" : soon ? ` (en ${daysLeft}d)` : ""}
                    </span>
                  )}
                </p>
              ) : (
                <p className="text-xs text-slate-400">Pendiente</p>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {d.uploaded && <button data-testid={`view-doc-${d.code}`} onClick={() => view(d.code)} className="p-1.5 text-slate-400 hover:text-blue-600" title="Ver"><Eye size={16} /></button>}
              {canEdit && d.uploaded && <button data-testid={`delete-doc-${d.code}`} disabled={busy === d.code} onClick={() => remove(d.code)} className="p-1.5 text-slate-400 hover:text-red-600" title="Eliminar"><Trash2 size={16} /></button>}
              {canEdit && (
                <>
                  <button data-testid={`upload-doc-${d.code}`} disabled={busy === d.code} onClick={() => fileRefs.current[d.code]?.click()} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-lg px-2.5 py-1.5">
                    {busy === d.code ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {d.uploaded ? "Reemplazar" : "Subir PDF"}
                  </button>
                  <input ref={(el) => (fileRefs.current[d.code] = el)} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => { upload(d, e.target.files[0]); e.target.value = ""; }} />
                </>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {expiryPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => setExpiryPrompt(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="expiry-prompt">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <CalendarClock size={18} className="text-amber-600" />
              <h3 className="font-display font-semibold text-slate-800">Fecha de vencimiento</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-600"><strong>{expiryPrompt.doc.name}</strong> requiere fecha de vencimiento del documento <span className="text-slate-400">({expiryPrompt.file.name})</span>.</p>
              <DateInput testid="expiry-date-input"
                value={expiryPrompt.date}
                onChange={(iso) => setExpiryPrompt((p) => ({ ...p, date: iso }))} />
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-100">
              <button onClick={() => setExpiryPrompt(null)} className="h-9 px-4 rounded-lg text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50">Cancelar</button>
              <button data-testid="confirm-expiry-upload" onClick={confirmExpiry}
                className="h-9 px-4 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700">
                Subir con esta fecha
              </button>
            </div>
          </div>
        </div>
      )}

      <DocumentPreviewModal open={!!preview} onClose={() => setPreview(null)}
        items={preview?.items} index={preview?.index}
        onIndexChange={(i) => setPreview((p) => ({ ...p, index: i }))} />
    </div>
  );
}
